import { FC, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { useShallow } from 'zustand/react/shallow'

import { Drawer, Stack, Typography } from '@mui/material'
import { HistoricalStatisticsFiltersForm } from '@/components/forms/HistoricalStatisticsFiltersForm/HistoricalStatisticsFiltersForm'
import {
  FiltersEntries,
  FiltersEntriesKey,
  initialFiltersEntries,
  PartialFiltersEntries,
  useHistoricalStatisticsStore
} from '@/stores/historicalStatistics'
import { onFieldChange } from '@/utils/forms/formik'

import { FiltersDrawerBottomControls } from './FiltersDrawerBottomControls'
import { FiltersDrawerTopControls } from './FIltersDrawerTopControls'

export const CONTROLS_HEIGHT = 58

export type ExtendedFiltersEntries = FiltersEntries & { isH2H: boolean }

type Props = {
  open: boolean
  onClose: () => void
}

export const FiltersDrawer: FC<Props> = ({ open: isOpen, onClose }) => {
  const { filtersEntries, setFiltersEntries, isH2H, setIsH2H, drawerPosition } =
    useHistoricalStatisticsStore(
      useShallow((state) => ({
        filtersEntries: state.filtersEntries,
        setFiltersEntries: state.setFiltersEntries,
        isH2H: state.isH2H,
        setIsH2H: state.setIsH2H,
        drawerPosition: state.drawerPosition
      }))
    )

  const [initialValues, setInitialValues] = useState<ExtendedFiltersEntries>({
    ...filtersEntries,
    isH2H
  })

  useEffect(() => {
    setInitialValues({ ...filtersEntries, isH2H })
  }, [filtersEntries])

  useEffect(() => {
    onFieldChange(formik, 'isH2H', isH2H)
  }, [isH2H])

  const formik = useFormik<ExtendedFiltersEntries>({
    initialValues,
    onSubmit: (values) => {
      setFiltersEntries(values)
      formik.resetForm({ values })
      updateInitialValues()
      onClose()
    }
  })

  const updateInitialValues = () => {
    setInitialValues((prev) => ({
      ...prev,
      isH2H
    }))
  }

  const handleFiltersChange = (newFilters: PartialFiltersEntries) => {
    Object.keys(newFilters).forEach((key) => {
      const filtersKey = key as FiltersEntriesKey
      const currentFilters = formik.values[filtersKey]

      const updatedFilters = {
        ...currentFilters,
        ...newFilters[filtersKey]
      }

      onFieldChange(formik, filtersKey, updatedFilters)
    })
  }

  const handleClose = () => {
    onClose()
    handleReset()
  }

  const handleReset = () => {
    formik.setValues(initialValues)
    setIsH2H(initialValues.isH2H)
  }

  const handleClear = () => {
    formik.setValues({ ...initialFiltersEntries, isH2H: false })
    setIsH2H(false)
  }

  return (
    <Drawer anchor={drawerPosition} open={isOpen} onClose={handleClose}>
      <Stack width={500} height="100vh">
        <FiltersDrawerTopControls
          onClose={handleClose}
          onClear={handleClear}
          onReset={handleReset}
          resetDisabled={!formik.dirty}
        />

        <Stack p={2} py={1} height="100%" overflow="auto">
          <Typography fontWeight={600} variant="h5" py={2}>
            Filters
          </Typography>
          <HistoricalStatisticsFiltersForm
            filters={formik.values.main}
            onChange={(newFilters) => {
              handleFiltersChange({ main: newFilters })
            }}
          />
          {isH2H && (
            <>
              <Typography fontWeight={700} variant="h6" sx={{ mx: 'auto', my: 2 }}>
                VS
              </Typography>
              <HistoricalStatisticsFiltersForm
                filters={formik.values.h2h}
                onChange={(newFilters) => {
                  handleFiltersChange({ h2h: newFilters })
                }}
              />
            </>
          )}
        </Stack>

        <FiltersDrawerBottomControls
          onClose={handleClose}
          onSubmit={() => {
            formik.handleSubmit()
          }}
          submitDisabled={!formik.dirty || !formik.isValid}
        />
      </Stack>
    </Drawer>
  )
}
